// PDF genérico de firma+impresión (Módulo H) — puerto de
// packages/hce/src/hce/consentimiento/pdfConsentimiento.ts
// (`generarPdfConsentimiento`, que pese al nombre ya era 100% genérico
// sobre cualquier Questionnaire+QuestionnaireResponse desde Fase 4). Usa
// el helper compartido de lib/pdfLayout.ts en vez de su propio wrapText/
// constantes locales.
//
// Tres modos de entrada — para que los Grupos 3/4 (001 y los formularios
// discretos, sin Questionnaire de por medio) puedan reusar el mismo
// generador+`PanelFirmaImpresion` sin una implementación de firma aparte por
// grupo:
//   (a) { questionnaire, response } — Grupos 1/2 (formularios con Questionnaire).
//   (b) { titulo, pares } — Grupos 3/4 (campo/valor plano, sin Questionnaire).
//   (c) { pares, datosEncabezadoMsp } — los 10 formularios del Anexo 1 MSP
//       (Grupo 4): mismo cuerpo pregunta/respuesta que (b), pero con el
//       encabezado/pie institucional del Anexo 1 (`pdfEncabezadoMsp.ts`) en
//       vez del genérico paciente/profesional/fecha.
import type { Questionnaire, QuestionnaireItem, QuestionnaireResponse } from '@medplum/fhirtypes';
import { dibujarEncabezadoMsp, dibujarEncabezadoMspCompacto, dibujarPieMsp, numerarHojasMsp, type DatosEncabezadoMsp } from './encabezadoMsp';
import { lineaContactoConsultorio } from './datosConsultorioHeader';
import type { FuentesPdf } from './fuentes';
import { crearCursorPdf, fechaHoraEc, GRIS_OSCURO, MUTED, NAVY, type CursorPdf } from './layout';
import { altoDibujoDenver, dibujarDenver, type DatosDenverPdf } from './denverII';
import { ALTO_DIBUJO_ODONTOGRAMA, dibujarOdontograma, type DatosOdontogramaPdf } from './odontograma';
import { altoDibujoPartograma, dibujarPartograma, partogramaTieneDatos, type DatosPartogramaPdf } from './partograma';
import { altoDibujoSilueta, dibujarSilueta, type DatosSiluetaPdf } from './siluetaCorporal';
import { altoDibujoGraficoConstantesUci, dibujarGraficoConstantesUci, type DatosGraficoConstantesUciPdf } from './graficoConstantesUci';
import { altoDibujoCurva, dibujarCurva, type DatosCurvasCrecimientoPdf } from './curvasCrecimiento';
import { altoDibujoSignosVitales, dibujarSignosVitales, type DatosSignosVitalesPdf } from './signosVitalesTimeline';
import { indexarRespuestas } from './responseUtils';

export interface DatosPdfFormulario {
  pacienteNombre: string;
  pacienteIdentificador?: string;
  practitionerNombre: string;
  practitionerRegistro: string;
  fechaISO: string;
  /** Dirección/teléfono/correo del consultorio (pedido de Sue, 2026-09-14) — el servidor los completa desde el tenant de la sesión, nunca el navegador. */
  direccionConsultorio?: string;
  telefonoConsultorio?: string;
  correoConsultorio?: string;
  /** Origen del formulario (059: cita MSP 2016; 034: propio de LogiaCare) — línea al pie, ver `fuenteFormularioPdf`. */
  fuenteFormulario?: string;
}

function textoRespuesta(valor: string | boolean | number): string {
  if (typeof valor === 'boolean') return valor ? 'Sí' : 'No';
  return String(valor);
}

/** Aplana Questionnaire.item (recursivo) a pares pregunta/respuesta legibles, en el mismo orden del formulario oficial. */
function paresPreguntaRespuesta(
  items: QuestionnaireItem[] | undefined,
  respuestas: Map<string, (string | boolean | number)[]>,
  out: { pregunta: string; respuesta: string }[],
): void {
  for (const item of items ?? []) {
    if (item.type !== 'group' && item.type !== 'display') {
      const valores = respuestas.get(item.linkId);
      if (valores?.length) {
        out.push({ pregunta: item.text ?? item.linkId, respuesta: valores.map(textoRespuesta).join(', ') });
      }
    }
    if (item.item) paresPreguntaRespuesta(item.item, respuestas, out);
  }
}

/** Las gráficas de curvas de crecimiento (Fenton/OMS/adolescentes), una debajo de otra; comparte el modo 'msp' y el 'pares'. */
function dibujarGraficasCurvas(cursor: CursorPdf, curvasCrecimiento: DatosCurvasCrecimientoPdf | undefined): void {
  for (const grafica of curvasCrecimiento?.graficas ?? []) {
    cursor.espacio(4);
    cursor.dibujo(altoDibujoCurva(grafica), (pagina, origen, anchoDisponible, fontRegular) => dibujarCurva(pagina, origen, anchoDisponible, fontRegular, grafica));
  }
}

async function armarPdf(titulo: string, pares: { pregunta: string; respuesta: string }[], datos: DatosPdfFormulario, fuentes: FuentesPdf, curvasCrecimiento?: DatosCurvasCrecimientoPdf): Promise<Uint8Array> {
  const cursor = await crearCursorPdf(fuentes);

  cursor.texto(titulo, { serif: true, size: 20, color: NAVY });
  cursor.espacio(4);
  cursor.linea();

  cursor.etiqueta('Paciente');
  cursor.texto(`${datos.pacienteNombre}${datos.pacienteIdentificador ? ` (${datos.pacienteIdentificador})` : ''}`, { bold: true, size: 12 });
  cursor.espacio(6);
  cursor.etiqueta('Profesional');
  cursor.texto(`${datos.practitionerNombre} — Registro MSP/ACESS ${datos.practitionerRegistro}`, { size: 11 });
  cursor.espacio(2);
  cursor.etiqueta('Fecha');
  cursor.texto(fechaHoraEc(datos.fechaISO), { size: 11 });
  const contactoConsultorio = lineaContactoConsultorio({
    direccion: datos.direccionConsultorio,
    telefono: datos.telefonoConsultorio,
    correo: datos.correoConsultorio,
  });
  if (contactoConsultorio) {
    cursor.espacio(2);
    cursor.texto(contactoConsultorio, { size: 8.5, color: MUTED });
  }
  cursor.espacio(10);
  cursor.linea();

  for (const { pregunta, respuesta } of pares) {
    cursor.texto(pregunta, { bold: true, size: 11, color: GRIS_OSCURO });
    cursor.texto(respuesta, { size: 11 });
    cursor.espacio(8);
  }

  dibujarGraficasCurvas(cursor, curvasCrecimiento);

  cursor.espacio(10);
  cursor.linea();
  cursor.texto(
    'Este documento respalda el registro guardado en el expediente clínico, que sigue siendo la fuente de verdad legal completa. La firma electrónica avanzada del profesional se agrega a continuación.',
    { size: 8, color: MUTED },
  );
  if (datos.fuenteFormulario) cursor.texto(datos.fuenteFormulario, { size: 8, color: MUTED });

  return cursor.guardar();
}

/** Modo (a) — formularios con Questionnaire (Grupos 1/2). */
export async function generarPdfFormulario(
  questionnaire: Questionnaire,
  response: QuestionnaireResponse,
  datos: DatosPdfFormulario,
  fuentes: FuentesPdf,
): Promise<Uint8Array> {
  return armarPdf(questionnaire.title ?? 'Formulario clínico', paresDeQuestionnaire(questionnaire, response), datos, fuentes);
}

/** Pares pregunta/respuesta de un Questionnaire+QuestionnaireResponse — para armar un documento en modo `msp` (encabezado del Anexo 1) desde un formulario con Questionnaire (024). */
export function paresDeQuestionnaire(questionnaire: Questionnaire, response: QuestionnaireResponse): { pregunta: string; respuesta: string }[] {
  const pares: { pregunta: string; respuesta: string }[] = [];
  paresPreguntaRespuesta(questionnaire.item, indexarRespuestas(response), pares);
  return pares;
}

/** Modo (b) — campo/valor plano, sin Questionnaire (Grupos 3/4: 001 y los formularios discretos). */
export async function generarPdfFormularioDePares(
  titulo: string,
  pares: { pregunta: string; respuesta: string }[],
  datos: DatosPdfFormulario,
  fuentes: FuentesPdf,
  curvasCrecimiento?: DatosCurvasCrecimientoPdf,
): Promise<Uint8Array> {
  return armarPdf(titulo, pares, datos, fuentes, curvasCrecimiento);
}

/**
 * Modo (c) — los 10 formularios del Anexo 1 MSP (Grupo 4): encabezado/pie institucional
 * (`dibujarEncabezadoMsp`/`dibujarPieMsp`) en vez del genérico "Paciente/Profesional/Fecha"
 * de `armarPdf`. `diagramaOdontograma` — solo 033: dibuja la grilla FDI vectorial
 * (`odontograma.ts`) después de los pares y antes del pie, mismo mecanismo pensado para
 * reusarse a futuro con otros diagramas bespoke (008/051).
 */
export async function generarPdfFormularioMsp(
  pares: { pregunta: string; respuesta: string }[],
  datosEncabezadoMsp: DatosEncabezadoMsp,
  datosFirma: { practitionerNombre: string; practitionerRegistro: string; fechaISO: string },
  fuentes: FuentesPdf,
  diagramaOdontograma?: DatosOdontogramaPdf,
  diagramaDenver?: DatosDenverPdf,
  diagramaSignosVitales?: DatosSignosVitalesPdf,
  diagramaPartograma?: DatosPartogramaPdf,
  diagramasGraficoConstantesUci?: readonly DatosGraficoConstantesUciPdf[],
  siluetaCorporal?: DatosSiluetaPdf,
  curvasCrecimiento?: DatosCurvasCrecimientoPdf,
): Promise<Uint8Array> {
  const cursor = await crearCursorPdf(fuentes);
  if (datosEncabezadoMsp.unaHoja) return generarPdfCertificadoUnaHoja(cursor, pares, datosEncabezadoMsp, datosFirma, fuentes);
  dibujarEncabezadoMsp(cursor, datosEncabezadoMsp);

  cursor.etiqueta('Profesional');
  cursor.texto(`${datosFirma.practitionerNombre} — ${datosEncabezadoMsp.etiquetaIdentificacion ?? 'Registro MSP/ACESS'} ${datosFirma.practitionerRegistro}`, { size: 11 });
  cursor.espacio(2);
  cursor.etiqueta('Fecha');
  cursor.texto(fechaHoraEc(datosFirma.fechaISO), { size: 11 });
  cursor.espacio(10);
  cursor.linea();

  for (const { pregunta, respuesta } of pares) {
    cursor.texto(pregunta, { bold: true, size: 11, color: GRIS_OSCURO });
    cursor.texto(respuesta, { size: 11 });
    cursor.espacio(8);
  }

  if (diagramaOdontograma && diagramaOdontograma.length > 0) {
    cursor.espacio(4);
    cursor.dibujo(ALTO_DIBUJO_ODONTOGRAMA, (pagina, origen, anchoDisponible, fontRegular) =>
      dibujarOdontograma(pagina, origen, anchoDisponible, fontRegular, diagramaOdontograma),
    );
  }

  if (diagramaDenver && diagramaDenver.entradas.length > 0) {
    cursor.espacio(4);
    cursor.dibujo(altoDibujoDenver(diagramaDenver), (pagina, origen, anchoDisponible, fontRegular) => dibujarDenver(pagina, origen, anchoDisponible, fontRegular, diagramaDenver));
  }

  if (diagramaSignosVitales && (diagramaSignosVitales.puntos.length > 0 || diagramaSignosVitales.eventos.length > 0)) {
    cursor.espacio(4);
    cursor.dibujo(altoDibujoSignosVitales(diagramaSignosVitales), (pagina, origen, anchoDisponible, fontRegular) =>
      dibujarSignosVitales(pagina, origen, anchoDisponible, fontRegular, diagramaSignosVitales),
    );
  }

  if (diagramaPartograma && partogramaTieneDatos(diagramaPartograma)) {
    cursor.espacio(4);
    cursor.dibujo(altoDibujoPartograma(diagramaPartograma), (pagina, origen, anchoDisponible, fontRegular) =>
      dibujarPartograma(pagina, origen, anchoDisponible, fontRegular, diagramaPartograma),
    );
  }

  if (siluetaCorporal && siluetaCorporal.regiones.length > 0) {
    cursor.espacio(4);
    cursor.dibujo(altoDibujoSilueta(siluetaCorporal), (pagina, origen, anchoDisponible, fontRegular) => dibujarSilueta(pagina, origen, anchoDisponible, fontRegular, siluetaCorporal));
  }

  for (const diagrama of diagramasGraficoConstantesUci ?? []) {
    if (!diagrama.series.some((s) => s.puntos.length > 0)) continue;
    cursor.espacio(4);
    cursor.dibujo(altoDibujoGraficoConstantesUci(diagrama), (pagina, origen, anchoDisponible, fontRegular) =>
      dibujarGraficoConstantesUci(pagina, origen, anchoDisponible, fontRegular, diagrama),
    );
  }

  dibujarGraficasCurvas(cursor, curvasCrecimiento);

  cursor.espacio(10);
  cursor.linea();
  cursor.texto(
    'Este documento respalda el registro guardado en el expediente clínico, que sigue siendo la fuente de verdad legal completa. La firma electrónica avanzada del profesional se agrega a continuación.',
    { size: 8, color: MUTED },
  );

  dibujarPieMsp(cursor, datosEncabezadoMsp);
  numerarHojasMsp(cursor.doc, await cursor.doc.embedFont(fuentes.sansRegular));
  return cursor.guardar();
}

/** Certificados (117, 116-cert): encabezado y pares compactos para que el documento entre en una sola hoja. */
async function generarPdfCertificadoUnaHoja(
  cursor: CursorPdf,
  pares: { pregunta: string; respuesta: string }[],
  datosEncabezadoMsp: DatosEncabezadoMsp,
  datosFirma: { practitionerNombre: string; practitionerRegistro: string; fechaISO: string },
  fuentes: FuentesPdf,
): Promise<Uint8Array> {
  dibujarEncabezadoMspCompacto(cursor, datosEncabezadoMsp);
  cursor.texto(`Profesional: ${datosFirma.practitionerNombre} — ${datosEncabezadoMsp.etiquetaIdentificacion ?? 'Registro MSP/ACESS'} ${datosFirma.practitionerRegistro} · Fecha: ${fechaHoraEc(datosFirma.fechaISO)}`, { size: 10 });
  cursor.espacio(2);
  cursor.linea();
  for (const { pregunta, respuesta } of pares) {
    cursor.texto(`${pregunta}: ${respuesta}`, { size: 10 });
    cursor.espacio(3);
  }
  cursor.espacio(4);
  cursor.linea();
  cursor.texto(
    'Este documento respalda el registro guardado en el expediente clínico, que sigue siendo la fuente de verdad legal completa. La firma electrónica avanzada del profesional se agrega a continuación.',
    { size: 7.5, color: MUTED },
  );
  dibujarPieMsp(cursor, datosEncabezadoMsp);
  numerarHojasMsp(cursor.doc, await cursor.doc.embedFont(fuentes.sansRegular));
  return cursor.guardar();
}
